import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import {
  ApiKey,
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyListResponse,
  ApiKeyScope,
} from '../types/apiKey';

export interface CreatedApiKey {
  plaintextKey: string;
  apiKey: ApiKey;
}

interface UseOrgApiKeysResult {
  keys: ApiKey[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  createKey: (name: string, scopes: ApiKeyScope[]) => Promise<CreatedApiKey>;
  revokeKey: (keyId: string) => Promise<void>;
}

export function useOrgApiKeys(organizationId?: string | null): UseOrgApiKeysResult {
  const api = useAuthenticatedApi();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    if (!organizationId) {
      setKeys([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<ApiKeyListResponse>(
        apiEndpoints.orgApiKeys.list(organizationId)
      );
      setKeys(response.api_keys || []);
    } catch (err: any) {
      console.error('Failed to load API keys:', err);
      setKeys([]);
      setError(err?.message || 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, [api, organizationId]);

  const createKey = useCallback(
    async (name: string, scopes: ApiKeyScope[]): Promise<CreatedApiKey> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }
      const payload: ApiKeyCreateRequest = { name, scopes };
      const response = await api.post<ApiKeyCreateResponse>(
        apiEndpoints.orgApiKeys.create(organizationId),
        payload
      );
      const { plaintext_key, ...apiKey } = response;
      // Optimistic prepend so the row appears immediately in the list.
      setKeys((prev) => [apiKey, ...prev]);
      return { plaintextKey: plaintext_key, apiKey };
    },
    [api, organizationId]
  );

  const revokeKey = useCallback(
    async (keyId: string): Promise<void> => {
      if (!organizationId) {
        throw new Error('No organization selected');
      }
      await api.delete(apiEndpoints.orgApiKeys.revoke(organizationId, keyId));
      setKeys((prev) => prev.filter((k) => k.id !== keyId));
    },
    [api, organizationId]
  );

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  return {
    keys,
    isLoading,
    error,
    reload: loadKeys,
    createKey,
    revokeKey,
  };
}
